import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { create } from "zustand";
import { ArrowUp, Check, Route, Sparkles, Square, TriangleAlert, X } from "lucide-react";
import { useRef, useState } from "react";

import { IaSwitch } from "../ai/IaSwitch";
import { api, type AssistantEvent, type AssistantRoute, background, streamAssistant } from "../api/client";
import { designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Spinner } from "../components/EmptyState";
import { Motion, Presence } from "../components/presence";
import { Badge, Button, FloatingPanel, IconButton, textareaClass } from "../components/ui";
import { HelpButton } from "../help/HelpButton";
import { k, lang, locale, t, tServer } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { aiSourceKey, plainAiText } from "./aiSource";
import { Waiting } from "../components/Waiting";

interface Step {
  id: string;
  label: string;
  state: "running" | "done" | "error";
}

// Zone de question en 13 px, celle du panneau IA de Prevail (§ 3.2), et sans poignée (elle vit dans le pied) : la
// zone de texte partagée, privée de sa taille de texte et de son redimensionnement (pas de variante 13 px partagée).
const QUESTION_CLASS = clsx(
  textareaClass
    .split(" ")
    .filter((c) => c !== "text-ui" && !c.startsWith("resize-"))
    .join(" "),
  "text-title resize-none",
);

// Questions proposées, posées dans la langue de l'interface (traduites à l'affichage et à l'envoi).
const SUGGESTIONS = [
  k("Résume la mission et l'idée de manœuvre."),
  k("Comment l'ennemi pourrait-il contre-attaquer ?"),
  k("Quels ponts principaux compte le camp ?"),
  k("Qu'est-ce qu'une LIMA ?"),
];

/** Dernière question posée à l'assistant pour un dossier, et sa réponse (diffusée ou reçue). */
interface AssistantSession {
  asked: string | null;
  steps: Step[];
  answer: string;
  error: string | null;
  elapsed: number | null;
  source: string | null;
  running: boolean;
  routeAsked: string | null;
}

const EMPTY_SESSION: AssistantSession = {
  asked: null,
  steps: [],
  answer: "",
  error: null,
  elapsed: null,
  source: null,
  running: false,
  routeAsked: null,
};

/** Réponses de l'assistant gardées pour la session, par dossier (audit de persistance m9) : cliquer une unité (la
 *  fiche prend la place du panneau), changer de vue ou de dossier ne perd plus une réponse de plusieurs dizaines de
 *  secondes, et une réponse qui arrive panneau fermé y est retrouvée. Non gardées après un rechargement. */
const useAssistantSessions = create<{ sessions: Record<string, AssistantSession> }>(() => ({ sessions: {} }));
const controllers = new Map<string, AbortController>();

function patchSession(key: string, change: Partial<AssistantSession> | ((s: AssistantSession) => Partial<AssistantSession>)) {
  useAssistantSessions.setState((state) => {
    const current = state.sessions[key] ?? EMPTY_SESSION;
    const next = typeof change === "function" ? change(current) : change;
    return { sessions: { ...state.sessions, [key]: { ...current, ...next } } };
  });
}

/** Assistant IA (panneau droit) : étapes cochées au fil de l'eau puis réponse diffusée, à la manière de Prevail ;
 *  l'ordre du dossier, la carte au temps affiché et la base doctrinale fondent la réponse ; ce qu'il a consulté
 *  n'est pas cité (décision d'Alexis du 29/10/2026 : aucune source citée dans Generex). */
export function AssistantPanel() {
  const toggleAssistant = useUi((s) => s.toggleAssistant);
  const exerciseId = useUi((s) => s.exerciseId);
  const mapSlug = useUi((s) => s.mapSlug);
  const phase = useUi((s) => s.phase);
  const route = useUi((s) => s.assistantRoute);
  const setRoute = useUi((s) => s.setAssistantRoute);
  const [question, setQuestion] = useState("");
  const sessionKey = exerciseId ?? "";
  const session = useAssistantSessions((st) => st.sessions[sessionKey]) ?? EMPTY_SESSION;
  const { asked, steps, answer, error, elapsed, source, running, routeAsked } = session;
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  const { data: status } = useQuery({ queryKey: ["ai-status"], queryFn: () => background(api.aiStatus), refetchInterval: 15_000 });

  const ask = async (q: string) => {
    const text = q.trim();
    if (!text || running) return;
    // La question et sa réponse appartiennent au dossier ouvert à l'envoi, même si l'on en change entre-temps.
    const key = sessionKey;
    setQuestion("");
    patchSession(key, { ...EMPTY_SESSION, asked: text, running: true, routeAsked: session.routeAsked });
    const controller = new AbortController();
    controllers.set(key, controller);
    const onEvent = (e: AssistantEvent) => {
      if (e.event === "step" && e.data.id) {
        const step: Step = { id: e.data.id, label: e.data.label ?? e.data.id, state: e.data.state ?? "running" };
        patchSession(key, (cur) => ({
          steps: cur.steps.some((s) => s.id === step.id) ? cur.steps.map((s) => (s.id === step.id ? step : s)) : [...cur.steps, step],
        }));
      } else if (e.event === "route" && e.data.runs && e.data.origin && e.data.destination && e.data.bounds) {
        if (useUi.getState().exerciseId === exerciseId) setRoute(e.data as AssistantRoute);
        patchSession(key, { routeAsked: text });
      } else if (e.event === "source") {
        patchSession(key, { source: e.data.label ?? null });
      } else if (e.event === "token") {
        patchSession(key, (cur) => ({ answer: cur.answer + (e.data.text ?? "") }));
      } else if (e.event === "error") {
        patchSession(key, { error: e.data.message ? plainAiText(tServer(e.data.message)) : t("Erreur") });
      } else if (e.event === "done") {
        patchSession(key, { elapsed: e.data.elapsed_s ?? null });
      }
    };
    try {
      await streamAssistant(
        { question: text, exercise_id: exerciseId, map_slug: mapSlug, phase, language: lang() },
        onEvent,
        controller.signal,
      );
    } catch (e) {
      if (!controller.signal.aborted) patchSession(key, { error: e instanceof Error ? plainAiText(e.message) : t("Erreur de connexion") });
    } finally {
      patchSession(key, { running: false });
      if (controllers.get(key) === controller) controllers.delete(key);
    }
  };

  // État de l'IA, en pastille courte (R6) : disponible (serveur GPU joignable), de secours (modèle du serveur),
  // en reprise (GPU en échec, IA de secours en démarrage : registre E29), indisponible.
  const statusBadge = status ? (
    status.available ? (
      aiSourceKey(status.source) !== "IA de secours" ? (
        <Badge dot tone="live">
          {t("IA disponible")}
        </Badge>
      ) : (
        <Badge dot tone="neutral">
          {t("IA de secours")}
        </Badge>
      )
    ) : status.resuming ? (
      <Badge dot tone="warn" title={t("Le serveur GPU ne répond pas : l'IA de secours démarre, elle sera prête dans une minute environ.")}>
        <Waiting>{t("Reprise de l'IA…")}</Waiting>
      </Badge>
    ) : (
      <Badge dot tone="warn">
        {t("IA indisponible")}
      </Badge>
    )
  ) : null;

  return (
    <FloatingPanel
      enter="right"
      width="assistant"
      tallHeader={Boolean(statusBadge)}
      icon={<Sparkles size={16} className="text-run" />}
      title={
        <span className="flex min-w-0 flex-col items-start gap-1 leading-tight">
          <span className="truncate">{t("Assistant IA")}</span>
          {statusBadge}
        </span>
      }
      actions={
        <>
          <IaSwitch className="mr-1" />
          <HelpButton screen="ia" buttonSize="sm" size={16} />
          <IconButton size="sm" onClick={toggleAssistant} title={t("Fermer")} aria-label={t("Fermer")}>
            <X size={16} />
          </IconButton>
        </>
      }
      footer={
        // Question : hors de la zone qui défile, toujours visible même après une longue réponse.
        <form
          className="flex min-w-0 flex-1 items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void ask(question);
          }}
        >
          <textarea
            ref={fieldRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void ask(question);
              }
            }}
            rows={2}
            placeholder={t("Posez une question…")}
            aria-label={t("Question à l'assistant")}
            className={clsx(QUESTION_CLASS, "min-w-0 flex-1")}
          />
          {running ? (
            <IconButton variant="outline" onClick={() => controllers.get(sessionKey)?.abort()} title={t("Arrêter")} aria-label={t("Arrêter")}>
              <Square size={14} />
            </IconButton>
          ) : (
            <IconButton
              type="submit"
              variant="primary"
              blocked={!question.trim()}
              onBlocked={() => designate(fieldRef.current, "field")}
              title={t("Envoyer")}
              aria-label={t("Envoyer")}
            >
              <ArrowUp size={16} />
            </IconButton>
          )}
        </form>
      }
    >
      <div className="space-y-3 px-4 py-3">
        {!asked && (
          <div className="space-y-2">
            <p className="text-title text-ink-muted">
              {t(
                "Questions sur l'ordre, la carte, le camp ou la doctrine. Les réponses s'appuient sur l'ordre du dossier, les unités au temps affiché ({phase}) et la base doctrinale : aide, glossaire, scénarios ME, rotation CENTAC.",
                { phase },
              )}
            </p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => void ask(t(s))}
                className="block w-full rounded-lg border border-line bg-canvas px-3 py-2 text-left text-title text-ink-soft transition-colors hover:bg-hover"
              >
                {t(s)}
              </button>
            ))}
          </div>
        )}
        {asked && (
          <>
            <Motion key={asked} origin="top" className="rounded-lg bg-canvas px-3 py-2 text-title text-ink">
              {asked}
            </Motion>
            <ol className="space-y-1.5">
              {steps.map((s) => (
                <li key={s.id}>
                  <Motion origin="fade" className="flex items-center gap-2 text-title text-ink-soft">
                    {s.state === "done" && <Check size={14} className="shrink-0 text-ok" />}
                    {s.state === "running" && <Spinner tone="run" decorative />}
                    {s.state === "error" && <TriangleAlert size={14} className="shrink-0 text-danger" />}
                    <span className={clsx(s.state === "running" && "font-medium text-ink")}>
                      {s.state === "running" ? <Waiting>{`${tServer(s.label)}…`}</Waiting> : `${tServer(s.label)}…`}
                    </span>
                  </Motion>
                </li>
              ))}
            </ol>
            {answer && (
              <Motion origin="fade" className="text-title leading-relaxed whitespace-pre-wrap text-ink">
                {answer}
              </Motion>
            )}
            <Presence show={Boolean(error)}>{error && <Callout tone="danger">{error}</Callout>}</Presence>
            <Presence show={Boolean(route && routeAsked === asked)}>
              {route && (
                <Motion origin="fade" className="flex items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-ui text-ink-soft">
                  <Route size={14} className="shrink-0 text-ink-muted" />
                  <span className="flex-1">
                    {t("Itinéraire tracé sur la carte ({phase}), {km} km", {
                      phase: route.phase,
                      km: (route.length_m / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 }),
                    })}
                  </span>
                  <Button variant="link" onClick={() => setRoute(null)}>
                    {t("Effacer")}
                  </Button>
                </Motion>
              )}
            </Presence>
            {elapsed !== null && (
              <div className="text-meta text-ink-muted">
                {t("Réponse en {s} s", { s: elapsed })}
                {source ? ` · ${t(aiSourceKey(source))}` : ""}
              </div>
            )}
          </>
        )}
      </div>
    </FloatingPanel>
  );
}
